import React, { useEffect, useMemo } from 'react'
import { subscribe } from 'react-contextual'
import { Heading, InfoList, Header } from '../../../../../views/components'
import EditIcon from '../../../../../views/components/atoms/Svgs/Edit'
import styles from './PropertyInfo.module.scss'
import { ContactsProvider } from 'modules/contacts'
import PropertiesProvider from 'modules/properties/PropertiesProvider'
import { useDispatch } from 'react-redux'
import { partyApiEvents } from 'modules/api/party'

type Owner = {
  ownerId?: string
  name?: string
  email?: string
  contactNumber?: string
  idNumber?: string
  bank?: React.ReactNode
  accountNumber?: string
}

type ViewData = {
  title?: string
  subTitle?: string
  owner: Owner
}

type PropertyInfoProps = {
  pathname: string
  propertyViewId: string
  redirect: (path: string) => void
  viewData: ViewData
  openEditProperty: (propertyViewId: string) => void
  openContactFromNonContactsRoute: (ownerId: string) => void
}

const PropertyInfo = ({
  propertyViewId,
  viewData,
  openEditProperty,
  openContactFromNonContactsRoute,
}: PropertyInfoProps) => {
  const { owner } = viewData

  const dispatch = useDispatch()

  useEffect(() => {
    if (!owner?.ownerId) {
      return
    }
    dispatch(partyApiEvents.party_request(owner.ownerId))
  }, [owner?.ownerId, dispatch])

  const infoListItem = useMemo(
    () => [
      {
        label: 'Name',
        value: owner.name,
        onClick: () => owner?.ownerId && openContactFromNonContactsRoute(owner.ownerId),
      },
      {
        label: 'Email',
        value: owner.email,
      },
      {
        label: 'Phone',
        value: owner.contactNumber,
      },
      {
        label: 'ID No.',
        value: owner.idNumber,
      },
    ],
    [owner, openContactFromNonContactsRoute],
  )

  const bankAccountItems = useMemo(
    () => [
      {
        label: 'Bank',
        value: owner.bank,
      },
      {
        label: 'Account',
        value: owner.accountNumber,
      },
    ],
    [owner],
  )

  return (
    <div className={styles.root}>
      <header>
        <button className={styles.edit} onClick={() => openEditProperty(propertyViewId)}>
          <EditIcon />
        </button>
        <div>
          <Heading as="h2" className={styles.title}>
            {viewData.title}
          </Heading>
          <Heading as="h3" className={styles['sub-title']}>
            {viewData.subTitle}
          </Heading>
        </div>
      </header>
      <section>
        <Header className={styles.header} tag="h4" text="Owner Information" />
        <InfoList items={infoListItem} />
      </section>
      <section>
        <Header className={styles.header} tag="h4" text="Linked Bank Account" />
        <InfoList spacing="none" items={bankAccountItems} />
      </section>
    </div>
  )
}

export default subscribe(
  [PropertiesProvider, ContactsProvider],
  ({ location, propertyViewId, redirect, openEditProperty }: any, { openContactFromNonContactsRoute }: any) => ({
    pathname: location.pathname,
    propertyViewId,
    redirect,
    openEditProperty,
    openContactFromNonContactsRoute,
  }),
)(PropertyInfo)
